/**
 * OPUS-913 — Form primitives (ported from the Claude Design handoff
 * primitives.jsx): Field (label + helper/error), TextInput, and the custom
 * keyboard-accessible Select. Coral focus ring; tokens throughout.
 */
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

export function Field({ label, required, children, helper, error, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)" }}>
            {label}{required && <span style={{ color: "var(--coral)", marginLeft: 3 }}>*</span>}
          </label>
          {hint && <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{hint}</span>}
        </div>
      )}
      {children}
      {error ? (
        <span style={{ fontSize: 11.5, color: "var(--coral)", display: "flex", alignItems: "center", gap: 5 }}>
          <Icon name="alertCircle" size={12} />{error}
        </span>
      ) : helper ? (
        <span style={{ fontSize: 11.5, color: "var(--text-dim)" }}>{helper}</span>
      ) : null}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, type = "text", invalid, icon, disabled }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 9, height: 40, padding: "0 12px",
      background: disabled ? "var(--surface-1)" : "var(--surface-2)",
      border: `1px solid ${invalid ? "var(--coral)" : focus ? "var(--coral)" : "var(--border)"}`,
      borderRadius: "var(--r-md)", transition: "border-color .14s", opacity: disabled ? 0.6 : 1, boxSizing: "border-box",
    }}>
      {icon && <Icon name={icon} size={15} style={{ color: "var(--text-dim)" }} />}
      <input
        value={value} onChange={(e) => onChange && onChange(e.target.value)} placeholder={placeholder} type={type} disabled={disabled}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13.5, fontFamily: "var(--font-sans)" }}
      />
    </div>
  );
}

export function Select({ value, onChange, options, placeholder = "Select…", width = "100%", label, icon, size = "md" }) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [coords, setCoords] = useState(null); // OPUS-913: fixed-position rect for the portalled menu
  const ref = useRef(null);
  const dropRef = useRef(null);
  const selected = options.find((o) => o.value === value);
  const h = size === "sm" ? 34 : 40;

  // OPUS-913: portal the menu to <body> so it isn't clipped by a modal's
  // overflow:auto. Position it from the trigger's viewport rect.
  const place = () => { if (ref.current) { const r = ref.current.getBoundingClientRect(); setCoords({ left: r.left, top: r.bottom + 6, width: r.width }); } };
  const openMenu = () => { place(); setOpen(true); };

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => { window.removeEventListener("scroll", reposition, true); window.removeEventListener("resize", reposition); };
  }, [open]);

  function onKey(e) {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) { e.preventDefault(); openMenu(); setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value))); return; }
    if (!open) return;
    if (e.key === "Escape") setOpen(false);
    else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(options.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (options[activeIdx]) { onChange(options[activeIdx].value); setOpen(false); } }
  }

  return (
    <div ref={ref} style={{ position: "relative", width }}>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 7 }}>{label}</label>}
      <button type="button" onClick={() => (open ? setOpen(false) : openMenu())} onKeyDown={onKey} aria-haspopup="listbox" aria-expanded={open}
        style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", height: h, padding: "0 12px",
          background: "var(--surface-2)", border: `1px solid ${open ? "var(--coral)" : "var(--border)"}`,
          borderRadius: "var(--r-md)", cursor: "pointer", color: "var(--text)", fontSize: 13.5, fontFamily: "var(--font-sans)", transition: "border-color .14s", textAlign: "left" }}>
        {icon && <Icon name={icon} size={15} style={{ color: "var(--text-dim)" }} />}
        <span style={{ flex: 1, color: selected ? "var(--text)" : "var(--text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: selected ? 600 : 400 }}>
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="chevronDown" size={16} style={{ color: "var(--text-dim)", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && coords && createPortal(
        <div ref={dropRef} role="listbox" className="opus-scroll" style={{ position: "fixed", top: coords.top, left: coords.left, width: coords.width, zIndex: 2000,
          background: "var(--elevated)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-lg)", padding: 6, maxHeight: 320, overflowY: "auto" }}>
          {options.length === 0 && <div style={{ padding: "9px 10px", fontSize: 13, color: "var(--text-dim)" }}>No options</div>}
          {options.map((o, i) => {
            const isSel = o.value === value;
            const isActive = i === activeIdx;
            return (
              <div key={o.value} role="option" aria-selected={isSel}
                onMouseEnter={() => setActiveIdx(i)} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: "var(--r-sm)", cursor: "pointer", background: isActive ? "var(--surface-3)" : "transparent" }}>
                {o.color && <span style={{ width: 9, height: 9, borderRadius: 3, background: o.color, flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: isSel ? 700 : 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</div>
                  {o.desc && <div style={{ fontSize: 11.5, color: "var(--text-dim)", marginTop: 2 }}>{o.desc}</div>}
                </div>
                {isSel && <Icon name="check" size={15} style={{ color: "var(--coral)" }} />}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

export default Field;
